import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle } from 'lucide-react'
import {
  getLibraryLessonComponents,
  type LcComponentStatus,
} from '@/api/lcLearning'
import type { LcLibraryReviewMode } from '@/api/lcLibraryReview'
import ComponentRunner from '@/components/learner/ComponentRunner'
import { useLearningTop } from '@/components/layout/LearningLayout'
import { getLearnerErrorMessage } from '@/utils/errorMessage'
import { Card } from '@/components/learner/ui/Card'
import { Button3D } from '@/components/learner/ui/Button3D'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import type { LibraryVideoReviewMode } from '@/components/learner/libraryVideoReview'
import { pickLocalizedText } from '@/utils/componentLabel'
import { isReviewable } from './model/libraryReviewModel'

export default function LibraryStepPlayer() {
  const { t, i18n } = useTranslation(['learner', 'common'])
  const navigate = useNavigate()
  const {
    setLessonLabel,
    setStepLabel,
    setProgressDots,
    setOnExit,
    setOnMenu,
  } = useLearningTop()
  const { lessonId, configComponentId } = useParams<{
    lessonId: string
    configComponentId: string
  }>()
  const [searchParams] = useSearchParams()
  const reviewMode = useMemo<LcLibraryReviewMode>(
    () => (searchParams.get('mode') === 'wrong_only' ? 'wrong_only' : 'full'),
    [searchParams],
  )
  const videoReviewMode = useMemo<LibraryVideoReviewMode>(
    () =>
      searchParams.get('video') === 'only'
        ? 'video_only'
        : 'video_with_block_quiz',
    [searchParams],
  )

  const [enrollmentId, setEnrollmentId] = useState<number | null>(null)
  const [component, setComponent] = useState<LcComponentStatus | null>(null)
  const [lessonLabel, setLibraryLessonLabel] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [quizCompletionReady, setQuizCompletionReady] = useState(false)
  const [exitConfirmationRequested, setExitConfirmationRequested] = useState(false)

  const load = useCallback(async () => {
    if (!lessonId || !configComponentId) {
      setError(t('learn.invalidPath'))
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setQuizCompletionReady(false)
    setExitConfirmationRequested(false)
    try {
      const detail = await getLibraryLessonComponents(Number(lessonId))
      const comp = detail.components.find(
        (candidate) =>
          candidate.configComponentId === configComponentId && isReviewable(candidate),
      )
      if (!comp) throw new Error('Component not found')
      setComponent(comp)
      setEnrollmentId(detail.enrollmentId)
      setLibraryLessonLabel(
        detail.lessonSequence != null
          ? t('library.lessonNumberTitle', {
              order: detail.lessonSequence,
              title: detail.lessonTitle,
            })
          : detail.lessonTitle,
      )
    } catch (e) {
      console.error('LibraryStepPlayer load failed', e)
      setError(getLearnerErrorMessage(e, t))
    } finally {
      setLoading(false)
    }
  }, [lessonId, configComponentId, t])

  useEffect(() => {
    load()
  }, [load])

  const handleComplete = useCallback(() => {
    navigate(`/my/library/${lessonId}`)
  }, [navigate, lessonId])

  const handleBack = useCallback(() => {
    if (quizCompletionReady) {
      setExitConfirmationRequested(true)
      return
    }
    navigate(`/my/library/${lessonId}`)
  }, [navigate, lessonId, quizCompletionReady])

  const handleExitWithoutComplete = useCallback(() => {
    setExitConfirmationRequested(false)
    navigate(`/my/library/${lessonId}`)
  }, [navigate, lessonId])

  const localizedStepLabel = useMemo(() => {
    if (!component) return ''
    return pickLocalizedText(component.label, component.labelEn, i18n.language)
      ?? t(`library.stepType.${component.type.toLowerCase()}`, {
        defaultValue: t('library.stepType.other'),
      })
  }, [component, i18n.language, t])

  useEffect(() => {
    setLessonLabel(lessonLabel)
    setStepLabel(localizedStepLabel)
    setProgressDots({ total: 0, currentIndex: -1, doneIndices: [] })
    setOnExit(handleBack)
    setOnMenu(null)

    return () => {
      setOnExit(null)
      setOnMenu(null)
    }
  }, [
    handleBack,
    lessonLabel,
    localizedStepLabel,
    setLessonLabel,
    setOnExit,
    setOnMenu,
    setProgressDots,
    setStepLabel,
  ])

  if (loading) {
    return <LearnerLoading />
  }

  if (error || !component || !lessonId || enrollmentId == null) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card
          size="md"
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center gap-4 py-8 text-center max-w-md"
        >
          <AlertCircle aria-label={t('aria.error')} className="size-10 text-lc-red-900" />
          <p className="text-lc-body-2 text-lc-gray-700">{error ?? t('learn.lessonNotFound')}</p>
          <Button3D variant="lightblue" size="M" onClick={load}>
            {t('common:retry')}
          </Button3D>
        </Card>
      </div>
    )
  }

  return (
    <>
      <ComponentRunner
        runtimeMode="library-review"
        reviewMode={reviewMode}
        videoReviewMode={videoReviewMode}
        headless
        enrollmentId={enrollmentId}
        lessonId={Number(lessonId)}
        component={component}
        isFirstAttempt={false}
        currentIndex={0}
        totalCount={1}
        onComplete={handleComplete}
        onBack={handleBack}
        exitConfirmationRequested={exitConfirmationRequested}
        onQuizCompletionReadyChange={setQuizCompletionReady}
        onExitWithoutComplete={handleExitWithoutComplete}
      />
    </>
  )
}
